import { forwardRef, useImperativeHandle, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import './Collapsible.css';

interface CollapsibleProps {
  containerStyle?: CSSProperties;
  initialState?: boolean;
  subcollapsible?: boolean;
  button?:
    | ReactNode
    | ((props: {
        handler: () => void;
        opened: boolean;
        close: () => void;
      }) => ReactNode);
  text?: (props: { opened: boolean }) => ReactNode;
  items?: ReactNode;
  onUpdateOpened?: (opened: boolean) => void;
}

export interface CollapsibleRef {
  open: () => void;
  close: () => void;
}

const Collapsible = forwardRef<CollapsibleRef, CollapsibleProps>(
  function Collapsible(
    {
      containerStyle,
      initialState = false,
      subcollapsible = false,
      button,
      text,
      items,
      onUpdateOpened
    },
    ref
  ) {
    const [opened, setOpened] = useState(!!initialState);

    function handleClick() {
      const next = !opened;
      setOpened(next);
      onUpdateOpened?.(next);
    }

    function close() {
      setOpened(false);
      onUpdateOpened?.(false);
    }

    useImperativeHandle(ref, () => ({
      open: () => {
        setOpened(true);
        onUpdateOpened?.(true);
      },
      close: () => {
        setOpened(false);
        onUpdateOpened?.(false);
      }
    }));

    const buttonClass = `Collapsible-button${opened ? ' is-opened' : ''}${subcollapsible ? ' Collapsible-sub' : ''}`;
    const chevron = !subcollapsible ? (
      <span className="Collapsible-chevron" />
    ) : (
      <div className="Collapsible-chevronColumn">
        <span
          className={`Collapsible-chevron Collapsible-chevronTop${opened ? ' is-opened' : ''}`}
        />
        <span
          className={`Collapsible-chevron Collapsible-chevronBottom${opened ? ' is-opened' : ''}`}
        />
      </div>
    );

    return (
      <div className="Collapsible">
        {typeof button === 'function' ? (
          // Render function: wrap output in clickable div, matching Vue's slot-inside-wrapper behaviour
          <button className={buttonClass} onMouseDown={handleClick}>
            {button({ handler: handleClick, opened, close })}
            {chevron}
          </button>
        ) : button ? (
          // Raw ReactNode: caller is responsible for wiring up the handler
          button
        ) : (
          <button className={buttonClass} onMouseDown={handleClick}>
            {text && text({ opened })}
            {chevron}
          </button>
        )}
        {opened && (
          <div className="Collapsible-container" style={containerStyle}>
            {items}
          </div>
        )}
      </div>
    );
  }
);

export default Collapsible;
